import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bell, MessageCircle, Heart, HeartCrack, Phone, Image, UserX, Shield, ShieldCheck, ShieldAlert, ShieldQuestion, AlertTriangle, Ban, Clock } from 'lucide-react';
import useNotificationsStore, { countsOnBell } from '@/stores/useNotificationsStore';
import { storyNotificationPath } from '@/utils/successStories';
import { isRtl } from '@/i18n/config';

/**
 * NotificationBell Component
 *
 * Displays a bell icon with unread count badge and dropdown
 * Uses Zustand store for global state - automatically updates in real-time
 *
 * Badge visibility: Shows only when unreadCount > 0
 * When dropdown opens, markAllAsRead is called which optimistically sets unreadCount to 0
 */
const NotificationBell = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Get data from Zustand store (auto-updates when store changes)
  const allNotifications = useNotificationsStore(state => state.notifications);
  const loading = useNotificationsStore(state => state.loading);
  const markAsRead = useNotificationsStore(state => state.markAsRead);
  const markAllAsRead = useNotificationsStore(state => state.markAllAsRead);

  const storeUnreadCount = useNotificationsStore(state => state.unreadCount);

  // Chat messages have their own bell (MessageBell).
  const notifications = allNotifications.filter(countsOnBell);
  // The SERVER's count, not this page's: the newest 50 can all be chat
  // messages while older notifications wait unread (BUG-521).
  const unreadCount = storeUnreadCount;

  // Mark all as read when dropdown opens - use useEffect for proper synchronization
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      markAllAsRead();
    }
  }, [isOpen, unreadCount, markAllAsRead]);

  const handleOpenChange = (open) => {
    setIsOpen(open);
  };

  // Only show loading on initial load (no cached notifications yet)
  const showLoading = loading && notifications.length === 0;

  // Badge visibility: Simply check unreadCount > 0
  // markAllAsRead optimistically sets unreadCount to 0, hiding the badge immediately
  const showBadge = unreadCount > 0;

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Story and couple notifications route through ONE helper, so the bell and
    // this list cannot send the same notification to two different screens.
    const storyPath = storyNotificationPath(notification);
    if (storyPath) {
      navigate(storyPath);
      return;
    }

    switch (notification.type) {
      case 'match_request':
        navigate('/messages?tab=requests');
        break;
      case 'match_accepted':
      case 'match_success':
      case 'conversation_activated':
      case 'new_message':
      case 'phone_request':
      case 'photo_request':
        if (notification.related_id) {
          navigate('/messages');
        }
        break;
      case 'conversation_closed':
      case 'blocked':
      case 'unmatched':
        navigate('/messages');
        break;
      case 'match_rejected':
      case 'match_queued':
        navigate('/messages?tab=requests');
        break;
      case 'form_approved':
        navigate('/dashboard');
        break;
      case 'profile_prompts_nudge':
        // "Answer two questions" — straight to the screen that has them.
        navigate(notification.related_id ? `/profile/edit/${notification.related_id}` : '/dashboard');
        break;
      case 'form_rejected':
        if (notification.related_id) {
          const reason = encodeURIComponent(notification.rejection_reason || notification.message || '');
          navigate(`/profile/edit/${notification.related_id}?rejected=true&reason=${reason}`);
        } else {
          navigate('/dashboard');
        }
        break;
      case 'id_verified':
      case 'id_rejected':
      case 'id_info_requested':
      case 'id_info_expired':
        // The ID section lives on /settings, and that is where the reason, the
        // re-upload and the support link are. This used to send all of them to
        // /messages — the same fault already recorded two cases below for
        // support_ticket_reply: the member taps a notification and lands
        // somewhere the thing they were told about does not exist. Mobile has
        // always deep-linked to the ID screen; this is web catching up.
        navigate('/settings');
        break;
      case 'admin_message':
        navigate('/notifications');
        break;
      case 'admin_chat':
      case 'admin_chat_closed':
        navigate('/messages?tab=admin');
        break;
      // The answer lives with the ticket (BUG-519), as NotificationsPage
      // already knew.
      case 'support_ticket_reply':
        navigate('/my-tickets');
        break;
      case 'warning':
      case 'suspension':
      case 'banned':
        navigate('/notifications');
        break;
      default:
        navigate('/notifications');
    }

    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'match_request':
      case 'match_accepted':
      case 'match_success':
      case 'conversation_activated':
        return <Heart className="h-4 w-4 text-pink-600" />;
      case 'match_queued':
        return <Heart className="h-4 w-4 text-blue-500" />;
      case 'match_rejected':
        return <HeartCrack className="h-4 w-4 text-gray-500" />;
      case 'new_message':
        return <MessageCircle className="h-4 w-4 text-blue-600" />;
      case 'phone_request':
        return <Phone className="h-4 w-4 text-green-600" />;
      case 'photo_request':
        return <Image className="h-4 w-4 text-purple-600" />;
      case 'conversation_closed':
      case 'blocked':
      case 'unmatched':
        return <UserX className="h-4 w-4 text-gray-500" />;
      case 'id_verified':
        return <ShieldCheck className="h-4 w-4 text-green-600" />;
      case 'id_rejected':
        return <ShieldAlert className="h-4 w-4 text-red-600" />;
      case 'id_info_requested':
        return <ShieldQuestion className="h-4 w-4 text-yellow-600" />;
      case 'id_info_expired':
        return <Clock className="h-4 w-4 text-gray-500" />;
      case 'admin_message':
        return <Shield className="h-4 w-4 text-blue-600" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'suspension':
        return <Clock className="h-4 w-4 text-orange-600" />;
      case 'banned':
        return <Ban className="h-4 w-4 text-red-600" />;
      case 'support_ticket_reply':
        return <MessageCircle className="h-4 w-4 text-blue-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return t('bells.timeNow');

    const now = new Date();
    const notificationTime = new Date(timestamp);

    if (isNaN(notificationTime.getTime())) return t('bells.timeNow');

    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('bells.timeNow');
    if (diffMins < 60) return t('bells.timeMinutes', { count: diffMins });
    if (diffHours < 24) return t('bells.timeHours', { count: diffHours });
    return t('bells.timeDays', { count: diffDays });
  };

  if (!isAuthenticated) {
    return null;
  }

  // Show only first 5 notifications in dropdown
  const displayNotifications = notifications.slice(0, 5);

  // The menu takes the PAGE's direction. Radix defaults to ltr, so both
  // menus were mirrored by hand for Arabic and read backwards in every
  // left-to-right language (2026-10-30).
  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange} dir={isRtl(i18n.language) ? 'rtl' : 'ltr'}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full h-9 w-9 md:h-10 md:w-10"
          aria-label={unreadCount > 0 ? t('bells.notificationsAriaUnread', { count: unreadCount }) : t('bells.notificationsAria')}
        >
          <Bell className="h-4 w-4 md:h-5 md:w-5" />
          {showBadge && (
            <span
              className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          <span className="sr-only" aria-live="polite" aria-atomic="true">
            {unreadCount > 0 ? t('bells.unreadNotifications', { count: unreadCount }) : t('bells.noUnreadNotifications')}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="px-4 py-3 border-b">
          <h3 className="font-bold text-wefaqna-navy text-lg text-start">{t('bells.notificationsTitle')}</h3>
        </div>

        {showLoading ? (
          <div className="p-4 text-center text-gray-500">{t('bells.loading')}</div>
        ) : displayNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>{t('bells.noNotifications')}</p>
          </div>
        ) : (
          <>
            {displayNotifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                  !notification.is_read ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex gap-3 w-full">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0 text-start">
                    <p className="font-medium text-sm text-wefaqna-navy truncate">
                      {notification.title}
                    </p>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {getTimeAgo(notification.created_at)}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  )}
                </div>
              </DropdownMenuItem>
            ))}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="px-4 py-3 text-center text-wefaqna-forest font-medium cursor-pointer"
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
            >
              {t('bells.viewAllNotifications')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
